import { Timestamp } from 'firebase/firestore'
import { Modify, PartialRecord, StringObject } from './GenericInterfaces'
import { BasicOption, Country, Culture, Language } from './Global'
import { SimplifiedAttribute, SimplifiedCategory, SimplifiedCollection, SimplifiedTag, SimplifiedTaxonomicObject } from './Taxonomy'
import { ProductAvailabilityOption } from './Settings'
import { EanOrientation } from './Ean'

export const ProductInfoTypes = ['consumerItem', 'baseUnit', 'innerPack', 'box', 'pallet'] as const
export type ProductInfoType = (typeof ProductInfoTypes)[number]

export enum ProductAvailabilityStatus {
  // 0, 8, 9 == statuses in Jeeves
  CONCEPT = -1,
  SELLABLE = 0,
  TO_BE_DISCONTINUED = 8,
  DISCONTINUED = 9,
}

export interface ProductAvailability {
  code: ProductAvailabilityStatus
  sellableWeb?: {
    from: string
  }
}

export const ProductTypes = ['product', 'component'] as const
export type ProductType = (typeof ProductTypes)[number]

// The structure of how products are stored in the database
export interface BaseProduct extends CultureSpecifics, Partial<Record<ProductInfoType, ProductInfo>> {
  type: ProductType

  sku: string
  recurrenceId?: string
  hasTemporarySku?: boolean
  relationKey?: string // swedish sku
  availableInCountries?: Country[]

  availability?: ProductAvailability
  mainLanguage?: Language

  createdBy?: string
  edited?: BaseEditedRecord[]

  collections?: string[]
  tags?: string[]
  categories?: string[]

  attributes?: {
    [id: string]: {
      staticValue?: string | number
      terms?: string[]
    }
  }

  propertyStatuses?: {
    [path: string]: ProductPropertyStatus
  }
}

export type DimensionUnit = 'length' | 'width' | 'height'

export type UnitDimensions = Partial<Record<DimensionUnit, number>>

export interface ProductInfo {
  dimensions?: UnitDimensions
  weight?: number
  barcode?: number | string
  isbn?: string
}

type PropertyKey = string

// The object returned from the backend with translated names for taxonomies and simplified structure
export interface SimplifiedProduct
  extends Modify<
      BaseProduct,
      {
        edited?: SimplifiedEditedRecord[]
        categories: SimplifiedCategory[]
        tags: SimplifiedTag[]
        collections: SimplifiedCollection[]
        attributes: SimplifiedProductAttribute[]

        languageSpecific?: Record<Language, ExtendedLanguageSpecifics>
        availability?: ProductAvailability & ProductAvailabilityOption & { css: string }
      }
    >,
    ExtendedLanguageSpecifics,
    CountrySpecifics {
  // mainName?: string

  // createTime: string
  updateTime?: string

  relatedProducts?: string[]

  images?: ProductImages
  versions?: Array<{ value: string; displayValue: string }>

  eanLabel?: AvailableEanLabelData | UnavailableAssetData
  flapData?: AvailableFlapData | UnavailableAssetData

  computed?: SimplifiedComputedValues
}

export type SimplifiedComputedValues = PartialRecord<PropertyKey, { options: Array<string | number> }>

export type UnavailableAssetData = {
  status: 'unavailable'
  reason: string
}

export type AvailableEanLabelData = {
  status: 'available'
  isInternational: boolean
  explicitCulture?: Culture
  brandLogoAsset?: string
  availableInCountries: Partial<Record<Country, CountrySpecificEanLabelData>>
}

export type CountrySpecificEanLabelData = {
  fsc: FscAsset
  showPriceGroup: boolean
}

export type FscAsset = Partial<Record<EanOrientation, string>>

export type AvailableFlapData = {
  status: 'available'
  calendariumAsset: string
  designEmblemAsset: string
  availableInCountries: Partial<Record<Country, unknown>>
}

export interface SimplifiedProductAttribute extends Omit<Omit<SimplifiedAttribute, 'terms'>, 'staticValues'> {
  options?: Array<SimplifiedTaxonomicObject | SimplifiedStaticValue>
}

export interface SimplifiedStaticValue extends Omit<SimplifiedTaxonomicObject, 'id'> {}

export const SimpleLanguageSpecificFields = [
  'name',
  'shortDescription',
  'description',
  'longDescription',
  'stmDescription',
  'uspsObject',
  'amazonTitle',
  'amazonKeywords',
] as const

export type LanguageSpecifics = Partial<Record<(typeof SimpleLanguageSpecificFields)[number], string> & { usps?: StringObject }>

export interface ExtendedLanguageSpecifics extends LanguageSpecifics {
  infoText?: string
}

export const CountrySpecificFields = ['basePrice', 'recommendedPriceInclVAT', 'recommendedPriceExclVAT', 'eCommercePrice', 'stmPrice', 'salePrice'] as const
export type CountrySpecifics = Partial<Record<(typeof CountrySpecificFields)[number], number>>

interface CultureSpecifics {
  languageSpecific?: Partial<Record<Language, LanguageSpecifics>>
  countrySpecific?: Partial<Record<Country, CountrySpecifics>>
}

interface BaseEditedRecord {
  user: string // user email
  time: Timestamp | string
  type?: 'script' | 'individual' | 'import'
}

export interface SimplifiedEditedRecord {
  user: string // user name or user email
  time: string
  type?: 'script' | 'individual' | 'import'
}

export const InfoTextTypes = ['jobout', 'stationery', 'calendars'] as const
export type InfoTextType = (typeof InfoTextTypes)[number]

export interface ProductImages {
  mainImg?: string
  gallery?: string[]
}

export const ProductPropertyStatuses = ['preliminary', 'checked'] as const
export type ProductPropertyStatus = (typeof ProductPropertyStatuses)[number]

export interface ProductsResponse<T = SimplifiedProduct> {
  total: number
  results: number
  data: T[]
  errored?: boolean
}
